/**
 * §A3 — the three progress stat boxes of the Reporting page.
 *
 * Each box renders what the spec lists and in the order it lists it: the big `NN.N%` figure
 * with its `%` in a `.sign` span, the fraction `N / {denominator}`, a progress track with a
 * fill bar, and the note. The heading, the panel note and the note text come from
 * `REPORT_PROGRESS_BOXES` in `@hockeytd/shared`, so the copy on the screen is the copy the
 * counting rule is attached to.
 *
 * **Nothing is counted here.** The counts and the denominator arrive from the server, which
 * derives them from the same snapshot as the pills below (§A3: the boxes *"cannot drift from
 * the pills below them"*). This component turns them into a percentage and a colour, using
 * the shared helpers the API's own tests assert against.
 *
 * **The denominator is never hard-coded.** §A2's `48` and the earlier `58` were both
 * examples; the fraction prints whatever the roster came to.
 */

import {
  REPORT_PROGRESS_BOXES,
  formatReportProgressPercent,
  reportProgressColour,
  reportProgressPercent,
  type ReportProgressBox,
  type ReportProgressCounts,
} from "@hockeytd/shared";

import { useCountUp } from "./useCountUp.js";

/**
 * The spec's note text carries `**…**` where it sets words in bold. Rendered rather than
 * printed, so the copy in `@hockeytd/shared` stays diffable against the spec.
 */
export function Emphasised({ text }: { text: string }): React.JSX.Element {
  const parts = text.split("**");
  return (
    <>
      {parts.map((part, index) =>
        index % 2 === 1 ? <b key={index}>{part}</b> : <span key={index}>{part}</span>,
      )}
    </>
  );
}

interface StatBoxProps {
  box: ReportProgressBox;
  count: number;
  denominator: number;
  /** `.panel--d1` … `.panel--d3` — the mockup staggers the three boxes as they fade up. */
  delayClass: string;
}

/**
 * One box.
 *
 * **It is a `.panel`, not a card of its own kind.** `reporting-page-v1.html` draws each box
 * as `section.panel.statbox` with a real `.panel__head` — title on the left, `.spacer`, panel
 * note on the right, over the head wash — and a padded `.statbox__body` under it. An earlier
 * build drew a bare bordered box with a hairline under an inline heading, which put three
 * cards on the page that belonged to no family the rest of the screen uses. The head is
 * restored so the three boxes and the two roster panels below them read as one set of panels.
 */
function StatBox({ box, count, denominator, delayClass }: StatBoxProps): React.JSX.Element {
  const pct = reportProgressPercent(count, denominator);
  const shown = useCountUp(pct);
  const colour = reportProgressColour(shown);

  return (
    <section
      className={`panel ${delayClass} statbox`}
      data-testid={`statbox-${box.key}`}
      // The settled figure, always present whatever the animation is doing. It is what a
      // test asserts on and what a screen reader is given below, so neither depends on a
      // frame callback having fired.
      data-pct={formatReportProgressPercent(pct)}
      aria-labelledby={`statbox-${box.key}-heading`}
    >
      <div className="panel__head">
        <h3 className="panel__title" id={`statbox-${box.key}-heading`}>
          {box.heading}
        </h3>
        <span className="spacer" />
        <span className="panel__note">{box.panelNote}</span>
      </div>

      <div className="statbox__body">
        <span className="pct num" style={{ color: colour }}>
          <span aria-hidden="true">{formatReportProgressPercent(shown)}</span>
          <span className="sign" aria-hidden="true">
            %
          </span>
          <span className="sr-only">
            {formatReportProgressPercent(pct)} per cent — {count} of {denominator} officials
          </span>
        </span>

        <span className="frac num" aria-hidden="true">
          {count} <span className="of">/ {denominator}</span>
        </span>

        <span className="track" aria-hidden="true">
          <i
            style={{ width: `${String(Math.min(100, Math.max(0, shown)))}%`, background: colour }}
          />
        </span>

        <span className="note">
          <Emphasised text={box.note} />
        </span>
      </div>
    </section>
  );
}

/** The mockup's stagger — `panel--d1`, `panel--d2`, `panel--d3`, in the grid's own order. */
const DELAY_CLASSES = ["panel--d1", "panel--d2", "panel--d3"];

export function ProgressStatBoxes({
  counts,
  denominator,
}: {
  counts: ReportProgressCounts;
  denominator: number;
}): React.JSX.Element {
  return (
    <div className="statgrid" data-howto="progress-boxes">
      {REPORT_PROGRESS_BOXES.map((box, index) => (
        <StatBox
          key={box.key}
          box={box}
          count={counts[box.key]}
          denominator={denominator}
          delayClass={DELAY_CLASSES[index] ?? "panel--d1"}
        />
      ))}
    </div>
  );
}
